import React, { useState, useEffect } from 'react';
import { Grid, Box, Typography, Alert } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import LoginForm from '../../components/LoginForm';
import imgLogo from '../../assets/images/logoElecnor.webp';
import ForgotPasswordModal from '../../components/ForgotPasswordModal';
import { loginUser, forgotPassword } from '../../api/authApi';
import { getMyCountries } from '../../api/countryApi';
import { useCountry } from '../../contexts/CountryContext';
import { getHomeRouteByRole } from '../../utils/roleRedirect';

const LoginPage = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [emailForPasswordRecovery, setEmailForPasswordRecovery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { setCountries, selectCountry } = useCountry();

  // Capturar errores que vengan desde el backend (Azure AD) en la URL
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const urlError = queryParams.get('error');

    if (urlError === 'user_not_found') {
      setError('El usuario de Microsoft no está registrado en el sistema SGR.');
    } else if (urlError === 'missing_email') {
      setError('No se pudo obtener el correo de la cuenta de Microsoft.');
    }
    else if (urlError === 'pending_admin') {
      setError('Tu cuenta fue reconocida por Microsoft, pero requieres aprobación. Por favor, comunícate con el Administrador para que complete tu registro en el sistema.');
    }
    else if (urlError === 'account_disabled') {
      setError('Tu cuenta fue dada de baja en el sistema SGR. Si necesitas volver a acceder, comunícate con el Administrador para que la reactive.');
    }
  }, [location]);

  const getRole = () => {
    var role = '';
    var user = localStorage.getItem('user');
    if (user) {
      role = JSON.parse(user).rols.description;
    }
    return role;
  }

  const handleLogin = async (email: string, password: string) => {
    setLoading(true);
    setError('');

    try {
      const response = await loginUser(email, password);

      if (response.success && response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
        localStorage.setItem('loginProvider', 'local');
        const userRole = getRole();

        // Multi-país: se resuelven los países del usuario.
        const countries = await getMyCountries();
        setCountries(countries);

        if (countries.length > 1) {
          // Varios países: el usuario elige con cuál trabajar (opción B).
          selectCountry(null);
          navigate('/seleccionar-pais');
        } else {
          // Un país (o ninguno, mono-país): se entra directo según el rol.
          selectCountry(countries.length === 1 ? countries[0] : null);
          navigate(getHomeRouteByRole(userRole));
        }
      } else {
        setError('Correo o contraseña incorrectos');
      }
    } catch (err) {
      setError('Ocurrió un error. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (email: string) => {
    setLoading(true);
    setError('');

    try {
      if (email) {
        const response = await forgotPassword(email);
        if (response.success) {
          setError('Correo de recuperación enviado');
          setIsModalOpen(false);
        } else {
          setError('Error al enviar el correo de recuperación');
        }
      } else {
        setError('Por favor ingresa un correo válido');
      }
    } catch (err) {
      setError('Ocurrió un error. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = () => {
    window.location.href = 'https://sgrchile-elecnor.duckdns.org/api/oauth2/authorization/azure';
  };

  return (
      <Grid
          container
          textAlign="center"
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: '100vh', backgroundColor: '#f4f6f8' }}
      >
        <Grid item xs={10} sm={6} md={3}>
          <Box mb={2}>
            <img src={imgLogo} alt="Elecnor Chile Logo" style={{ maxWidth: '100%' }} />
          </Box>

          <Typography variant="h5" gutterBottom sx={{ color: '#004F86' }}>
            <strong>Sistema Gestión Rendimientos</strong>
          </Typography>

          {/* Mostrar alertas de error si existen */}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <LoginForm
              onSubmit={handleLogin}
              error={error}
              loading={loading}
              onForgotPassword={() => setIsModalOpen(true)}
              onMicrosoftLogin={handleMicrosoftLogin}
          />

          <ForgotPasswordModal
              open={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onSubmit={handleForgotPassword}
              setEmailForPasswordRecovery={setEmailForPasswordRecovery}
          />
        </Grid>
      </Grid>
  );
};

export default LoginPage;